/**
 * useLayout (B10) — runs inside <AppLayout/>.
 *
 *  - Detects the layout variant from the viewport width and ?kiosk=1 (§A.1) and
 *    publishes it: actions.setLayout (desktop | mobile | kiosk) and actions.setKiosk.
 *  - Measures env(safe-area-inset-*) with a probe element.
 *  - Publishes the camera's safe insets (§A.6) with actions.setInsets on resize,
 *    selection (card open → right inset), sheet snap and drive.
 *
 * Framing at t = 0 (§A.6 "select() frames with the card-open right inset from
 * t = 0"): store listeners run in subscription order, so a listener that reacts
 * to `selectedId` may run before the new insets are published. The bridge / B4
 * should read `getApp().insets` (not the listener's state argument) or call
 * `frameInsets({ cardOpen: true })`, which is exact whatever the order.
 */
import { useEffect, useState } from "react";
import type { AppState, Insets, SheetSnap } from "@/contracts/store";
import { actions, appStore, getApp } from "@/store/store";
import {
  computeInsets,
  detectVariant,
  kioskFromSearch,
  METRICS,
  panelYields,
  type InsetInput,
  type LayoutMetrics,
  type LayoutVariant,
  type SheetMode,
} from "./layoutMath";

export interface LayoutInfo {
  variant: LayoutVariant;
  metrics: LayoutMetrics;
  kiosk: boolean;
  vw: number;
  vh: number;
  /** env(safe-area-inset-*) in px. */
  safe: Insets;
}

const ZERO: Insets = { top: 0, right: 0, bottom: 0, left: 0 };
const HAS_DOM = typeof window !== "undefined" && typeof document !== "undefined";

function viewportNow(): { vw: number; vh: number } {
  if (!HAS_DOM) return { vw: 1440, vh: 900 };
  return { vw: window.innerWidth || document.documentElement.clientWidth, vh: window.innerHeight || document.documentElement.clientHeight };
}

function infoFor(vw: number, vh: number, kiosk: boolean, safe: Insets): LayoutInfo {
  const variant = detectVariant(vw, kiosk);
  return { variant, metrics: METRICS[variant], kiosk, vw, vh, safe };
}

let current: LayoutInfo | null = null;

/** The last measured layout (null before <AppLayout/> mounts). */
export function currentLayout(): LayoutInfo | null {
  return current;
}

/**
 * The side panel is stepped aside (narrow desktop, a place selected): AppLayout hides it and the left
 * inset drops to G + B. It stays aside through a drive from that card, so it does not come and go twice.
 */
export function panelAside(s: Pick<AppState, "selectedId">, info: Pick<LayoutInfo, "variant" | "vw">): boolean {
  return !!s.selectedId && panelYields(info.variant, info.vw);
}

/**
 * Store state → inset input. The card is gone during a drive (§E.6). On mobile the
 * sheet steps aside in a drive, but the bottom edge keeps the peek inset: the
 * DriveHud (h 56 + gaps) sits there.
 */
export function insetInputFor(s: Pick<AppState, "selectedId" | "phase" | "sheet">, info: LayoutInfo): InsetInput {
  const driving = s.phase === "drive";
  const cardOpen = !!s.selectedId && !driving;
  return {
    variant: info.variant,
    vw: info.vw,
    vh: info.vh,
    cardOpen,
    panelHidden: panelAside(s, info),
    sheet: driving ? "peek" : s.sheet,
    sheetMode: cardOpen ? "card" : "panel",
    safe: info.safe,
  };
}

/**
 * Safe insets for a framing, from the current layout and store, with overrides.
 * `frameInsets({ cardOpen: true })` = the select framing, even before the store's
 * insets have caught up with the selection.
 */
export function frameInsets(over: { cardOpen?: boolean; sheet?: SheetSnap; sheetMode?: SheetMode } = {}): Insets {
  const s = getApp();
  if (!current) return s.insets;
  const input = insetInputFor(s, current);
  if (over.cardOpen !== undefined) {
    input.cardOpen = over.cardOpen;
    input.panelHidden = over.cardOpen ? panelYields(current.variant, current.vw) : input.panelHidden;
    input.sheetMode = over.cardOpen ? "card" : "panel";
    // a newly opened card on mobile arrives at half (§A.4)
    if (over.cardOpen && current.variant === "mobile" && !s.selectedId) input.sheet = "half";
  }
  if (over.sheet) input.sheet = over.sheet;
  if (over.sheetMode) input.sheetMode = over.sheetMode;
  return computeInsets(input);
}

/* ── env(safe-area-inset-*) probe ─────────────────────────────────── */

function createProbe(): HTMLElement | null {
  if (!HAS_DOM) return null;
  const el = document.createElement("div");
  el.setAttribute("aria-hidden", "true");
  el.dataset.ldSafeProbe = "";
  el.style.cssText =
    "position:fixed;left:0;top:0;width:0;height:0;visibility:hidden;pointer-events:none;" +
    "padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px);";
  document.body.appendChild(el);
  return el;
}

function readSafe(probe: HTMLElement | null): Insets {
  if (!probe) return ZERO;
  const cs = getComputedStyle(probe);
  const px = (v: string) => Math.round(parseFloat(v) || 0);
  return { top: px(cs.paddingTop), right: px(cs.paddingRight), bottom: px(cs.paddingBottom), left: px(cs.paddingLeft) };
}

const sameInsets = (a: Insets, b: Insets) => a.top === b.top && a.right === b.right && a.bottom === b.bottom && a.left === b.left;
const sameInfo = (a: LayoutInfo | null, b: LayoutInfo) =>
  !!a && a.variant === b.variant && a.kiosk === b.kiosk && a.vw === b.vw && a.vh === b.vh && sameInsets(a.safe, b.safe);

/* ── the hook ─────────────────────────────────────────────────────── */

export function useLayout(): LayoutInfo {
  const [kiosk] = useState(() => HAS_DOM && kioskFromSearch(location.search));
  const [info, setInfo] = useState<LayoutInfo>(() => {
    const { vw, vh } = viewportNow();
    return infoFor(vw, vh, kiosk, current?.safe ?? ZERO);
  });

  // Viewport + safe area. rAF-throttled; one probe per mount, removed on unmount.
  useEffect(() => {
    if (!HAS_DOM) return undefined;
    const probe = createProbe();
    let raf = 0;
    const measure = () => {
      raf = 0;
      const { vw, vh } = viewportNow();
      const next = infoFor(vw, vh, kiosk, readSafe(probe));
      if (!sameInfo(current, next)) {
        current = next;
        setInfo(next);
      }
    };
    const onResize = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    current = null; // force the first publish of this mount
    measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      probe?.remove();
    };
  }, [kiosk]);

  // Coarse layout + kiosk flag for everyone else.
  const layout = info.metrics.layout;
  useEffect(() => {
    if (getApp().layout !== layout) actions.setLayout(layout);
    if (getApp().kiosk !== kiosk) actions.setKiosk(kiosk);
  }, [layout, kiosk]);

  // Safe insets: now, and whenever the selection, sheet or phase changes.
  useEffect(() => {
    current = info;
    const publish = (s: AppState) => actions.setInsets(computeInsets(insetInputFor(s, info)));
    publish(getApp());
    return appStore.subscribe((s, p) => {
      if (s.selectedId !== p.selectedId || s.sheet !== p.sheet || s.phase !== p.phase) publish(s);
    });
  }, [info]);

  return info;
}
